import cn from 'classnames';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import attentionCat from '@/shared/assets/images/cats/attention.png';
import { useMedia } from '@/shared/hooks';
import { AuthCard, Button } from '@/shared/ui';

import { MFAEnabledNotice } from '../MFAEnabledNotice/MFAEnabledNotice';
import { MFAEnrollmentCard } from '../MFAEnrollmentCard/MFAEnrollmentCard';

import styles from './ForcedMFAEnrollment.module.scss';

type ForcedMFAEnrollmentStep = 'notice' | 'setup' | 'enabled';

type ForcedMFAEnrollmentProps = {
  className?: string;
  /** Named on the notice so the user knows which organization imposes the requirement. */
  requiredByOrganizationName?: string;
  isVerificationRequired?: boolean;
  onCompleted: () => void;
  onSignOut: () => void;
};

/**
 * The blocking enrolment run: the account has nowhere to land without a code,
 * so the only ways out are finishing setup or signing out.
 */
export const ForcedMFAEnrollment: FC<ForcedMFAEnrollmentProps> = (props) => {
  const {
    className,
    requiredByOrganizationName,
    isVerificationRequired = false,
    onCompleted,
    onSignOut,
  } = props;

  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  const [step, setStep] = useState<ForcedMFAEnrollmentStep>('notice');

  if (step === 'enabled' && !isVerificationRequired) {
    return (
      <MFAEnabledNotice
        className={className}
        onDone={onCompleted}
        message={
          <FormattedMessage
            id="mfa.forced.enabled"
            defaultMessage="Two-step verification is enabled. You will be asked for a code at every sign in."
          />
        }
      />
    );
  }

  if (step === 'setup' && !isVerificationRequired) {
    return (
      <MFAEnrollmentCard
        className={className}
        withSupportHint
        onCompleted={() => setStep('enabled')}
        onSignInRequired={onSignOut}
        onCancel={onSignOut}
        eyebrow={
          <FormattedMessage
            id="mfa.forced.stepTwo"
            defaultMessage="Step 2 of 2"
          />
        }
        title={
          <FormattedMessage
            id="mfa.setup.title"
            defaultMessage="Set up two-step verification"
          />
        }
        cancelLabel={
          <FormattedMessage id="global.signOut" defaultMessage="Sign out" />
        }
      />
    );
  }

  return (
    <AuthCard
      variant="notice"
      className={className}
      dataSection="mfa-forced-notice"
    >
      {!isVerificationRequired && (
        <span className={cn(styles.eyebrow, 'p4')}>
          <FormattedMessage
            id="mfa.forced.stepOne"
            defaultMessage="Step 1 of 2"
          />
        </span>
      )}

      <h3 className={isMobile ? 'h5' : 'h3'}>
        <FormattedMessage
          id="mfa.forced.title"
          defaultMessage="Two-step verification is now required"
        />
      </h3>

      <img
        src={attentionCat}
        width={130}
        height={130}
        alt={formatMessage({
          id: 'imgAltText.attentionCat',
          defaultMessage: 'Attention cat',
        })}
      />

      <p className={cn(styles.description, 'p2')}>
        {isVerificationRequired ? (
          <FormattedMessage
            id="mfa.forced.verifyDescription"
            defaultMessage="Your authenticator is already set up. Sign in again and enter its code to verify this session."
          />
        ) : (
          <FormattedMessage
            id="mfa.forced.description"
            defaultMessage="A 6-digit code at sign in is required to continue to Diagnocat. Set up an authenticator app now — it takes about a minute."
          />
        )}
      </p>

      {requiredByOrganizationName && (
        <div className={styles.requiredBy}>
          <span className={cn(styles.requiredByLabel, 'p3')}>
            <FormattedMessage
              id="mfa.forced.requiredBy"
              defaultMessage="Required by"
            />
          </span>

          <span className="p3b">{requiredByOrganizationName}</span>
        </div>
      )}

      <div className={styles.actions}>
        {!isVerificationRequired && (
          <Button variant="tertiary" onClick={onSignOut}>
            <FormattedMessage id="global.signOut" defaultMessage="Sign out" />
          </Button>
        )}

        <Button
          onClick={isVerificationRequired ? onSignOut : () => setStep('setup')}
        >
          {isVerificationRequired ? (
            <FormattedMessage
              id="mfa.forced.signInAgain"
              defaultMessage="Sign in again"
            />
          ) : (
            <FormattedMessage
              id="mfa.forced.setUpNow"
              defaultMessage="Set up now"
            />
          )}
        </Button>
      </div>
    </AuthCard>
  );
};
